import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { c, btn } from '../../theme';

// Create, edit or delete a billing scenario: a name and a percentage change
// per charge category. Blank or 0 means the category is unchanged.
//
// Saved, not held in the page: a scenario is something finance discusses and
// comes back to, and the board, the list and the exports all read it from the
// server by id so they cannot disagree about what it is.
export default function ScenarioEditor({ scenario, categories, onClose, onSaved, onDeleted }) {
  const qc = useQueryClient();
  const nameRef = useRef(null);
  const [name, setName] = useState(scenario?.name || '');
  const [description, setDescription] = useState(scenario?.description || '');
  const [rates, setRates] = useState(() => Object.fromEntries(
    categories.map((x) => [x.key, scenario?.rates?.[x.key] !== undefined ? String(scenario.rates[x.key]) : ''])
  ));

  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const body = () => ({
    name: name.trim(),
    description: description.trim() || null,
    rates: Object.fromEntries(Object.entries(rates).filter(([, v]) => v !== '' && Number(v) !== 0).map(([k, v]) => [k, Number(v)])),
  });
  const invalid = categories.find((x) => rates[x.key] !== '' && (!Number.isFinite(Number(rates[x.key])) || Math.abs(Number(rates[x.key])) > 100));

  const refresh = () => qc.invalidateQueries({ queryKey: ['billing-scenarios'] });

  const save = useMutation({
    mutationFn: ({ asNew }) => (scenario && !asNew
      ? api.put(`/customer-billing/scenarios/${scenario.id}`, body())
      : api.post('/customer-billing/scenarios', body())),
    onSuccess: async (saved) => { await refresh(); toast.success(`Saved "${saved.name}".`); onSaved(saved); },
    onError: (err) => toast.error(err.message || 'Could not save the scenario.'),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/customer-billing/scenarios/${scenario.id}`),
    onSuccess: async () => { await refresh(); toast(`Deleted "${scenario.name}".`); onDeleted(); },
    onError: (err) => toast.error(err.message || 'Could not delete the scenario.'),
  });
  const busy = save.isPending || remove.isPending;
  const canSave = name.trim() && !invalid && !busy;

  return (
    <>
      <div style={s.scrim} onClick={onClose} aria-hidden="true" />
      <aside style={s.panel} role="dialog" aria-modal="true" aria-label={scenario ? `Edit scenario ${scenario.name}` : 'New scenario'}>
        <div style={s.head}>
          <h2 style={s.h2}>{scenario ? 'Edit scenario' : 'New scenario'}</h2>
          <button type="button" style={btn.secondaryXs} onClick={onClose}>Close</button>
        </div>

        <label style={s.field}>
          <span style={s.label}>Name</span>
          <input id="scenario-name" ref={nameRef} value={name} maxLength={120} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. FY27 rate adjustment" style={s.input} />
        </label>
        <label style={s.field}>
          <span style={s.label}>Notes</span>
          <textarea id="scenario-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                    placeholder="What this assumes, where the numbers came from" style={{ ...s.input, height: 'auto', padding: '8px 10px', resize: 'vertical' }} />
        </label>

        <div>
          <div style={s.label}>Change by charge category</div>
          <p style={s.help}>
            Each line on a customer&apos;s bill changes by its category&apos;s percentage. Leave a category blank to keep it as billed.
            Disposal, overfill, overweight, contamination and late fees are usually left alone: they pass through or depend on what happened that month.
          </p>
          <div style={s.rates}>
            {categories.map((x) => (
              <label key={x.key} style={s.rateRow}>
                <span style={s.rateLabel}>{x.label}</span>
                <span style={s.rateInputWrap}>
                  <input id={`rate-${x.key}`} inputMode="decimal" value={rates[x.key]} aria-label={`${x.label} percent change`}
                         onChange={(e) => setRates((r) => ({ ...r, [x.key]: e.target.value.replace(/[^0-9.-]/g, '') }))}
                         placeholder="0" style={s.rateInput} />
                  <span style={s.pct}>%</span>
                </span>
              </label>
            ))}
          </div>
          {invalid && <div style={s.error}>{invalid.label}: enter a percentage between −100 and 100.</div>}
        </div>

        <div style={s.actions}>
          <button type="button" style={{ ...btn.primarySm, opacity: canSave ? 1 : 0.5 }} disabled={!canSave}
                  onClick={() => save.mutate({ asNew: false })}>
            {save.isPending ? 'Saving…' : scenario ? 'Save changes' : 'Save scenario'}
          </button>
          {scenario && (
            <button type="button" style={{ ...btn.secondarySm, opacity: canSave ? 1 : 0.5 }} disabled={!canSave}
                    onClick={() => save.mutate({ asNew: true })}>
              Save as new
            </button>
          )}
          {scenario && (
            <button type="button" style={{ ...btn.danger, marginLeft: 'auto' }} disabled={busy}
                    onClick={() => { if (window.confirm(`Delete "${scenario.name}"? Anyone using it will lose it too.`)) remove.mutate(); }}>
              Delete
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

const s = {
  scrim:   { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.25)', zIndex: 40 },
  panel:   { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(460px, 100vw)', background: c.surface, zIndex: 41, overflowY: 'auto', padding: '18px 20px', boxShadow: '-8px 0 24px rgba(15, 23, 42, 0.12)', display: 'flex', flexDirection: 'column', gap: '14px' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  h2:      { margin: 0, fontSize: '17px', fontWeight: 650, color: c.text },
  field:   { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  input:   { height: '34px', fontSize: '13px', fontFamily: 'inherit', padding: '0 10px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', color: c.text },
  help:    { fontSize: '12px', color: c.textMuted, lineHeight: 1.5, margin: '4px 0 8px' },
  rates:   { display: 'flex', flexDirection: 'column', border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'hidden' },
  rateRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '6px 10px', borderBottom: `1px solid ${c.canvas}` },
  rateLabel: { fontSize: '13px', color: c.textStrong },
  rateInputWrap: { display: 'flex', alignItems: 'center', gap: '4px' },
  rateInput: { width: '72px', height: '30px', fontSize: '13px', fontFamily: 'inherit', textAlign: 'right', padding: '0 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontVariantNumeric: 'tabular-nums' },
  pct:     { fontSize: '12px', color: c.textMuted },
  error:   { fontSize: '12px', color: c.dangerText, marginTop: '6px' },
  actions: { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' },
};
